import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Plus, TrendingUp, Scale, Ruler, Activity, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { fetchApi, formatDate } from '../lib/utils';
import { useLanguage } from '../context/LanguageContext';

const METRICS = {
  weight: { label: 'Weight', unit: 'kg', icon: Scale, color: '#CCFF00' },
  waist: { label: 'Waist', unit: 'cm', icon: Ruler, color: '#14B8A6' },
  chest: { label: 'Chest', unit: 'cm', icon: Ruler, color: '#6366F1' },
  arms: { label: 'Arms', unit: 'cm', icon: Ruler, color: '#F59E0B' },
  thighs: { label: 'Thighs', unit: 'cm', icon: Ruler, color: '#EF4444' },
  run_5k: { label: '5K Run', unit: 'min', icon: Activity, color: '#10B981' },
  run_10k: { label: '10K Run', unit: 'min', icon: Activity, color: '#8B5CF6' },
};

export default function Progress() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [progress, setProgress] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState('weight');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [newEntry, setNewEntry] = useState({
    metric_name: 'weight',
    value: '',
    notes: '',
  });

  useEffect(() => {
    loadProgress();
  }, []);

  const loadProgress = async () => {
    try {
      const [progressData, summaryData] = await Promise.all([
        fetchApi('/progress'),
        fetchApi('/progress/summary')
      ]);
      setProgress(progressData);
      setSummary(summaryData);
    } catch (error) {
      console.error('Load progress error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newEntry.value) return;
    
    setSubmitting(true);
    try {
      const metric = METRICS[newEntry.metric_name];
      await fetchApi('/progress', {
        method: 'POST',
        body: JSON.stringify({
          entry_type: newEntry.metric_name.includes('run') ? 'performance' : 'measurement',
          metric_name: newEntry.metric_name,
          value: parseFloat(newEntry.value),
          unit: metric.unit,
          notes: newEntry.notes,
        }),
      });
      setDialogOpen(false);
      setNewEntry({ metric_name: 'weight', value: '', notes: '' });
      loadProgress();
    } catch (error) {
      console.error('Submit error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const getChartData = () => {
    return progress
      .filter(p => p.metric_name === selectedMetric)
      .sort((a, b) => new Date(a.recorded_at) - new Date(b.recorded_at))
      .slice(-30)
      .map(p => ({
        date: formatDate(p.recorded_at),
        value: p.value,
      }));
  };

  const getLatestValue = (metric) => {
    const entries = progress.filter(p => p.metric_name === metric);
    if (entries.length === 0) return null;
    return entries.sort((a, b) => new Date(b.recorded_at) - new Date(a.recorded_at))[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const chartData = getChartData();
  const currentMetric = METRICS[selectedMetric];

  return (
    <div className="min-h-screen bg-black pb-8">
      <div className="noise-overlay" />
      
      {/* Header */}
      <header className="sticky top-0 z-50 glass">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-zinc-400 hover:text-white"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Dashboard</span>
          </button>
          <h1 className="text-lg font-bold">Progress Tracking</h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                size="sm"
                className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full"
                data-testid="log-progress-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Log
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle>Log Progress</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-zinc-300">Metric</Label>
                  <Select
                    value={newEntry.metric_name}
                    onValueChange={(v) => setNewEntry({ ...newEntry, metric_name: v })}
                  >
                    <SelectTrigger className="mt-2 bg-zinc-800 border-zinc-700" data-testid="metric-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(METRICS).map(([key, metric]) => (
                        <SelectItem key={key} value={key}>{metric.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-300">Value ({METRICS[newEntry.metric_name].unit})</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={newEntry.value}
                    onChange={(e) => setNewEntry({ ...newEntry, value: e.target.value })}
                    placeholder={`Enter ${METRICS[newEntry.metric_name].label.toLowerCase()}`}
                    className="mt-2 bg-zinc-800 border-zinc-700"
                    data-testid="value-input"
                    required
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Notes (optional)</Label>
                  <Input
                    value={newEntry.notes}
                    onChange={(e) => setNewEntry({ ...newEntry, notes: e.target.value })}
                    placeholder="Any notes about this entry"
                    className="mt-2 bg-zinc-800 border-zinc-700"
                    data-testid="notes-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full"
                  data-testid="submit-progress-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Entry'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Summary Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8"
        >
          <div className="card-default" data-testid="streak-summary">
            <p className="text-sm text-zinc-400 mb-1">Streak</p>
            <p className="text-3xl font-black">{summary?.streak_days || 0}</p>
            <p className="text-xs text-zinc-500">days</p>
          </div>
          <div className="card-default" data-testid="workouts-summary">
            <p className="text-sm text-zinc-400 mb-1">This Month</p>
            <p className="text-3xl font-black">{summary?.workouts_this_month || 0}</p>
            <p className="text-xs text-zinc-500">workouts</p>
          </div>
          <div className="card-default" data-testid="weight-summary">
            <p className="text-sm text-zinc-400 mb-1">Current Weight</p>
            <p className="text-3xl font-black">{summary?.latest_weight || '—'}</p>
            <p className="text-xs text-zinc-500">kg</p>
          </div>
          <div className="card-default" data-testid="entries-summary">
            <p className="text-sm text-zinc-400 mb-1">Total Entries</p>
            <p className="text-3xl font-black">{progress.length}</p>
            <p className="text-xs text-zinc-500">logged</p>
          </div>
        </motion.div>

        {/* Metric Selector */}
        <div className="mb-6 overflow-x-auto hide-scrollbar">
          <div className="flex gap-2 pb-2">
            {Object.entries(METRICS).map(([key, metric]) => (
              <button
                key={key}
                onClick={() => setSelectedMetric(key)}
                className={`px-4 py-2 rounded-full whitespace-nowrap transition-all flex items-center gap-2 ${
                  selectedMetric === key
                    ? 'bg-[#CCFF00] text-black font-bold'
                    : 'bg-zinc-900 text-zinc-400 hover:bg-zinc-800'
                }`}
                data-testid={`metric-${key}`}
              >
                <metric.icon className="w-4 h-4" />
                {metric.label}
              </button>
            ))}
          </div>
        </div>

        {/* Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-default mb-8"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold">{currentMetric.label} Progress</h3>
            {getLatestValue(selectedMetric) && (
              <span className="text-sm text-zinc-400">
                Latest: {getLatestValue(selectedMetric).value} {currentMetric.unit}
              </span>
            )}
          </div>
          
          {chartData.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" strokeOpacity={0.3} />
                  <XAxis
                    dataKey="date"
                    stroke="#52525B"
                    fontSize={12}
                    tickLine={false}
                  />
                  <YAxis
                    stroke="#52525B"
                    fontSize={12}
                    tickLine={false}
                    domain={['auto', 'auto']}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#18181B',
                      border: '1px solid #27272A',
                      borderRadius: '8px',
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke={currentMetric.color}
                    strokeWidth={2}
                    dot={{ fill: currentMetric.color, strokeWidth: 0, r: 4 }}
                    activeDot={{ r: 6, fill: currentMetric.color }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-zinc-500">
              <div className="text-center">
                <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No {currentMetric.label.toLowerCase()} data yet</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setNewEntry({ ...newEntry, metric_name: selectedMetric });
                    setDialogOpen(true);
                  }}
                  className="mt-2 text-[#CCFF00]"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Log your first entry
                </Button>
              </div>
            </div>
          )}
        </motion.div>

        {/* Recent Entries */}
        <h3 className="font-bold mb-4">Recent Entries</h3>
        <div className="space-y-3">
          {progress.slice(0, 10).map((entry, index) => {
            const metric = METRICS[entry.metric_name] || { label: entry.metric_name, icon: Activity };
            return (
              <motion.div
                key={entry.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center gap-4 p-4 rounded-xl bg-zinc-900/50 border border-zinc-800"
                data-testid={`entry-${index}`}
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${metric.color || '#CCFF00'}20` }}
                >
                  <metric.icon className="w-5 h-5" style={{ color: metric.color || '#CCFF00' }} />
                </div>
                <div className="flex-1">
                  <p className="font-medium">{metric.label}</p>
                  <p className="text-sm text-zinc-400">{formatDate(entry.recorded_at)}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold tabular-nums">{entry.value}</p>
                  <p className="text-xs text-zinc-500">{entry.unit}</p>
                </div>
              </motion.div>
            );
          })}
          
          {progress.length === 0 && (
            <div className="text-center py-8 text-zinc-500">
              <p>No progress entries yet. Start logging to track your journey!</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
